import Loading from "@/components/Loading";
import { AuthSignUpPageContainer } from "@/components/pages/Auth/AuthSignUpPageOutlet";
import { Retailer } from "@/redux/types";
import {
  brandclub_colors,
  getEntityImage,
  UNIVERSAL_STORE_SITE,
} from "@brandclub/common-ui";
import { canResetPassword, openResetPassword } from "@/utils/retailerUtils";

import {
  Box,
  IconButton,
  InputAdornment,
  styled,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";

import { useAppSelector } from "@/redux/hooks";

import { StyledLink } from "@/components/AppNavigation/StyledLink";
import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";
import {
  ButtonLoadingEllipsis,
  OutlineButton,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { useGuestCheckoutEnabled } from "../../useGuestCheckout";

const Visibility = () => {
  const theme = useTheme();

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="23.999"
      height="18"
      viewBox="0 0 23.999 18"
      style={{
        fill: theme.vars.palette.text.primary,
        marginBottom: 1,
      }}
    >
      <g data-name="Group 22676">
        <path
          data-name="Path 25917"
          d="M23.821 8.181C22.943 6.261 19.5 0 12 0S1.057 6.261.179 8.181a1.969 1.969 0 0 0 0 1.64C1.057 11.739 4.5 18 12 18s10.943-6.261 11.821-8.181a1.968 1.968 0 0 0 0-1.638zM12 16c-6.307 0-9.25-5.366-10-6.989C2.75 7.366 5.693 2 12 2c6.292 0 9.236 5.343 10 7-.764 1.657-3.708 7-10 7z"
        />
        <path
          data-name="Path 25918"
          d="M12 4a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0 8a3 3 0 1 1 3-3 3 3 0 0 1-3 3z"
        />
      </g>
    </svg>
  );
};

const VisibilityOff = () => {
  const theme = useTheme();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="23.999"
      height="23.391"
      viewBox="0 0 23.999 23.391"
      fill={theme.vars.palette.text.primary}
    >
      <g data-name="Group 22469">
        <path
          data-name="Path 25917"
          d="M23.821 10.837C22.943 8.917 19.5 2.656 12 2.656S1.057 8.917.179 10.837a1.969 1.969 0 0 0 0 1.64C1.057 14.395 4.5 20.656 12 20.656s10.943-6.261 11.821-8.181a1.968 1.968 0 0 0 0-1.638zM12 18.656c-6.307 0-9.25-5.366-10-6.989.75-1.645 3.693-7.011 10-7.011 6.292 0 9.236 5.343 10 7-.764 1.657-3.708 7-10 7z"
        />
        <path
          data-name="Path 25918"
          d="M12 6.656a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0 8a3 3 0 1 1 3-3 3 3 0 0 1-3 3z"
        />
        <path
          data-name="Line 1092"
          transform="translate(1.489 1.414)"
          style={{
            fill: "none",
            stroke: theme.vars.palette.text.primary,
            strokeLinecap: "round",
            strokeWidth: 2,
          }}
          d="M21.023 0 0 20.563"
        />
      </g>
    </svg>
  );
};
const Container = styled(AuthSignUpPageContainer)(({ theme }) => ({
  color: theme.vars.palette.text.primary,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    marginTop: 20,
  },
  ".retailer-connect-login-container": {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },

  ".connect-title": {
    fontWeight: 700,
    fontSize: 22,
    marginBottom: "12px",
    [theme.breakpoints.down("sm")]: {
      fontSize: 18,
      marginBottom: "8px",
    },
    textAlign: "center",
  },
  ".connect-subtitle": {
    fontSize: 16,
    textAlign: "center",
    fontWeight: 500,
    marginBottom: "25px",
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
      marginBottom: "25px",
    },
  },
  ".reset-password-button": {
    background: "transparent",
    border: "none",

    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
    margin: "16px 0 8px 0",
    color: theme.vars.palette.text.primary,

    textDecoration: "underline",
    "&:disabled": {
      color: "text.disabled",
      cursor: "not-allowed",
    },
  },
}));

export const RetailerConnectLogin = ({
  guestCheckoutClick,
  onSaveCredentials,
  onChange,
  closeEdit,
  currentUsername,
  currentPassword,
  retailer,
  customSubtitle,
  errorBorder,
}: {
  guestCheckoutClick?: () => void;
  onSaveCredentials: (username: string, password: string) => Promise<void>;
  onChange?: (event: {
    username: string;
    password: string;
    edited: boolean;
  }) => void;
  closeEdit: () => void;
  currentUsername: string;
  currentPassword: string;
  retailer?: Retailer;
  customSubtitle?: string;
  errorBorder?: boolean;
}) => {
  const theme = useTheme();
  const [errorInput, setErrorInput] = useState<boolean>(!!errorBorder);
  const [username, setUsername] = useState<string>(currentUsername);
  const [password, setPassword] = useState<string>(currentPassword);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const domainConfig = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig
  );
  const { experimentEnabled } = useGuestCheckoutEnabled(retailer?.retailerId);

  useEffect(() => {
    if (errorBorder) {
      setErrorInput(true);
    }
  }, [errorBorder]);

  const linkPrefix =
    domainConfig?.storeBrandingType === "CustomDTCStore"
      ? UNIVERSAL_STORE_SITE
      : "";

  const edited = username !== currentUsername || password !== currentPassword;

  useEffect(() => {
    onChange?.({ username, password, edited });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, username, edited]);

  const getUserNameFieldHeading = (retailer: Retailer) => {
    if (retailer.retailerLoginUsernameFieldTypes?.length) {
      const supportedFieldTypes = retailer.retailerLoginUsernameFieldTypes;
      if (
        supportedFieldTypes.includes("email") &&
        supportedFieldTypes.includes("phoneNumber")
      ) {
        return "Email or mobile phone";
      }
    }
    return "Email";
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Container>
      {!retailer ? (
        <Box
          className="retailer-connect-login-container"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Loading dot />
        </Box>
      ) : (
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          maxWidth={478}
        >
          <Box
            className="retailer-connect-login-container"
            sx={{
              maxWidth: 380,
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "column",
              },
              alignItems: "center",
              justifyContent: "flex-start",
              bgcolor: "white",
              borderRadius: 4,
            }}
          >
            {/* Retailer Icon */}

            {retailer.logo ? (
              <img
                alt="r_logo"
                src={retailer.logo}
                style={{
                  maxWidth: 162,
                  maxHeight: 72,
                  objectFit: "contain",
                  marginBottom: 35,
                }}
              />
            ) : (
              <Box
                sx={{
                  bgcolor: "#e6f3f7",
                  borderRadius: "50%",
                  width: 72,
                  height: 72,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: "35px",
                }}
              >
                <img
                  className="retailer_logo"
                  alt="r_logo"
                  style={{
                    width: "72px",
                    height: "72px",
                    objectFit: "contain",
                  }}
                  src={getEntityImage(retailer?.retailerId, "retailer")}
                />
              </Box>
            )}

            {/* Title */}
            <Box className="connect-title">
              Log in to {retailer?.displayName || "Retailer"}
            </Box>
            {/* Subtitle */}
            <Box className="connect-subtitle">
              {customSubtitle ||
                `Connect your ${
                  retailer?.displayName || "Retailer"
                } account to earn rewards and get expedited shipping.`}
            </Box>
            {/* Inputs */}
            <Box sx={{ width: "100%", mb: "25px" }}>
              <CheckoutInput
                label={getUserNameFieldHeading(retailer || ({} as Retailer))}
                style={{ marginBottom: 16 }}
                onChange={(e) => {
                  setErrorInput(false);
                  setUsername(e.target.value as any);
                }}
                sx={{
                  ".MuiFilledInput-root": {
                    borderColor: errorInput
                      ? theme.vars.palette.error.main
                      : theme.vars.palette.divider,
                  },
                }}
                disabled={submitting}
                value={username}
                InputProps={{
                  autoComplete: "username",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }}
              />
              <CheckoutInput
                label="Password"
                type={showPassword ? "text" : "password"}
                style={{ marginBottom: 0 }}
                onChange={(e) => {
                  setErrorInput(false);
                  setPassword(e.target.value as any);
                }}
                disabled={submitting}
                value={password}
                sx={{
                  ".MuiFilledInput-root": {
                    borderColor: errorInput
                      ? theme.vars.palette.error.main
                      : theme.vars.palette.divider,
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disableRipple
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          size="small"
                          disabled={submitting}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
            {/* Disclaimer */}

            {/* Buttons */}
            <SolidButton
              className="checkout-solid-button"
              disableMaxWidth
              sx={{
                width: "100%",
              }}
              onClick={
                submitting
                  ? undefined
                  : async () => {
                      setSubmitting(true);
                      await onSaveCredentials(username, password ?? "");
                      setSubmitting(false);
                      closeEdit();
                    }
              }
              disabled={!username || !password}
            >
              {submitting ? (
                <ButtonLoadingEllipsis
                  baseFontSize={16}
                  customColor={"#ffffff"}
                />
              ) : (
                "Continue"
              )}
            </SolidButton>
            {canResetPassword(retailer) && (
              <Box
                component="button"
                onClick={() => openResetPassword(retailer)}
                disabled={submitting}
                className="reset-password-button"
              >
                Reset your {retailer?.displayName || "Retailer"} password
              </Box>
            )}
          </Box>
          <Box
            sx={() => ({
              fontSize: "12px",
              mb: "24px",
              mt: "4px",
              opacity: 0.5,
              textAlign: "center",
            })}
          >
            By providing your {retailer?.displayName || "Retailer"} credentials,
            you authorize Brandclub to place this order on your behalf in
            accordance with our{" "}
            <StyledLink to={linkPrefix + "/terms"} target="_blank">
              Terms
            </StyledLink>
            .
          </Box>
          {experimentEnabled ? (
            guestCheckoutClick && errorBorder ? (
              <Box
                sx={{
                  bgcolor: brandclub_colors.blueWhite,
                  borderRadius: "8px",
                  px: "20px",
                  py: "16px",
                  textAlign: "center",
                  width: "100%",
                  boxSizing: "border-box",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: "8px" }}>
                  Having trouble logging in?
                </Box>
                <Box sx={{ fontSize: 14, fontWeight: 500 }}>
                  Don’t worry — if we can’t connect your{" "}
                  {retailer?.displayName || "Retailer"} account, your order will
                  still process and ship on time with guest checkout. Additional
                  fees may apply.
                </Box>
                <OutlineButton
                  className="checkout-outline-button"
                  sx={{
                    backgroundColor: "#fff",
                    marginTop: "10px",
                    width: "100%",
                    maxWidth: "380px",
                    ":hover": {
                      backgroundColor: "#fff",
                    },
                  }}
                  onClick={() => {
                    guestCheckoutClick();
                    closeEdit();
                  }}
                >
                  Switch to guest checkout
                </OutlineButton>
              </Box>
            ) : (
              <Box
                sx={{
                  bgcolor: brandclub_colors.blueWhite,
                  borderRadius: "8px",
                  px: "20px",
                  py: "16px",
                  textAlign: "center",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ fontWeight: 600, fontSize: 16, mb: "8px" }}>
                  Can’t log in?
                </Box>
                <Box sx={{ fontSize: 14, fontWeight: 500 }}>
                  Don’t worry — if we can’t connect your{" "}
                  {retailer?.displayName || "Retailer"} account, your order will
                  still process and ship on time with guest checkout. Additional
                  fees may apply.
                </Box>
              </Box>
            )
          ) : null}
        </Box>
      )}
    </Container>
  );
};
